import React, { useEffect, useRef, useState, memo, useMemo } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import Supercluster from 'supercluster';
import { FireIncident } from '@/hooks/use-fires-data';
import { Button } from '@/components/ui/button';
import { Locate, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { getMapboxTokenFromAnySource } from '@/utils/mapbox-helper';

interface FiresMapV2Props {
  incidents: FireIncident[] | null;
  radius: number;
  location?: string;
}

const FiresMapV2 = ({ 
  incidents, 
  radius,
  location = 'nsw-australia'
}: FiresMapV2Props) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMapReady, setIsMapReady] = useState(false);
  const [userLocation, setUserLocation] = useState<{lat: number, lng: number} | null>(null);
  
  // Initialize supercluster
  const supercluster = useMemo(() => 
    new Supercluster({
      radius: 75,
      maxZoom: 16,
      minPoints: 2
    }), []
  );
  
  // Default center coordinates based on location
  const getDefaultCenter = (): [number, number] => {
    switch(location) {
      case 'nsw-australia':
        return [151.2093, -33.8688]; // Sydney [lng, lat]
      case 'victoria-australia':
        return [144.9631, -37.8136]; // Melbourne
      case 'queensland-australia':
        return [153.0251, -27.4698]; // Brisbane
      case 'california-usa':
        return [-119.4179, 36.7783]; // California
      case 'colorado-usa':
        return [-105.7821, 39.5501]; // Colorado
      case 'germany':
        return [10.4515, 51.1657]; // Germany
      case 'france':
        return [2.2137, 46.2276]; // France
      default:
        return [151.2093, -33.8688]; // Default to Sydney
    }
  };
  
  // Get user's location
  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userCoords = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(userCoords);
          
          if (mapRef.current && isMapReady) {
            mapRef.current.flyTo({
              center: [userCoords.lng, userCoords.lat],
              zoom: 10,
              duration: 2000
            });
            
            // Add user location marker
            new mapboxgl.Marker({ color: '#3b82f6' })
              .setLngLat([userCoords.lng, userCoords.lat])
              .setPopup(new mapboxgl.Popup().setHTML('<p class="font-semibold">Your Location</p>'))
              .addTo(mapRef.current);
          }
        },
        (error) => {
          console.error("Error getting user location:", error);
        }
      );
    }
  };
  
  // Initialize map with proper load event handling
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;
    
    const token = getMapboxTokenFromAnySource();
    if (!token) {
      console.error('FiresMapV2: No Mapbox token available');
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Set token right before map creation
      mapboxgl.accessToken = token;
      
      // Create map instance with exact pattern as working maps
      const map = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: 'mapbox://styles/mapbox/outdoors-v12',
        center: getDefaultCenter(),
        zoom: 8,
        attributionControl: false
      });
    
      mapRef.current = map;
      
      // Critical: Handle load event properly
      map.on('load', () => {
        console.log('FiresMapV2: Map loaded successfully');
        
        // Add navigation controls after load
        try {
          map.addControl(new mapboxgl.NavigationControl(), 'top-right');
          map.addControl(new mapboxgl.ScaleControl({ maxWidth: 200 }), 'bottom-left');
          map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-right');
        } catch (err) {
          console.warn('Controls may already exist:', err);
        }
        
        // Add radius circle source and layer
        map.addSource('radius-circle', {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: getDefaultCenter()
            },
            properties: {}
          }
        });
        
        map.addLayer({
          id: 'radius-circle-fill',
          type: 'fill',
          source: 'radius-circle',
          paint: {
            'fill-color': '#ff0000',
            'fill-opacity': 0.1
          }
        });
        
        map.addLayer({
          id: 'radius-circle-line',
          type: 'line',
          source: 'radius-circle',
          paint: {
            'line-color': '#ff0000',
            'line-width': 2,
            'line-opacity': 0.5
          }
        });
        
        setIsMapReady(true);
        setIsLoading(false);
      });
      
      // Handle map errors
      map.on('error', (e) => {
        console.error('FiresMapV2: Map error:', e);
        // Ignore non-critical errors
        if (e.error?.message?.includes('403') || e.error?.message?.includes('terrain')) {
          return;
        }
      });
      
    } catch (error) {
      console.error('FiresMapV2: Failed to initialize map:', error);
      setIsLoading(false);
    }
    
    // Cleanup
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        setIsMapReady(false);
      }
    };
  }, []); // Only run once on mount
  
  // Helper function to create marker element
  const createMarkerElement = (cluster: any, pointCount: number) => {
    const el = document.createElement('div');
    
    if (cluster.properties.cluster) {
      // Cluster marker
      el.className = 'cluster-marker';
      el.style.cssText = `
        width: ${20 + (pointCount / incidents!.length) * 20}px;
        height: ${20 + (pointCount / incidents!.length) * 20}px;
        background: #ef4444;
        border: 3px solid white;
        border-radius: 50%;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        color: white;
        font-weight: bold;
        font-size: ${pointCount > 99 ? '10px' : '12px'};
      `;
      el.innerHTML = pointCount > 99 ? '99+' : pointCount.toString();
    } else {
      // Individual marker
      el.className = 'fire-marker';
      el.style.cssText = `
        width: 24px;
        height: 24px;
        background: #ef4444;
        border: 2px solid white;
        border-radius: 50%;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      `;
      el.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="white"><path d="M19.48 12.35c-1.57-4.08-7.16-4.3-5.81-10.23c.1-.44-.37-.78-.75-.55C9.29 3.71 6.68 8 8.87 13.62c.18.46-.36.89-.75.59c-1.81-1.37-2-3.34-1.84-4.75c.06-.52-.62-.77-.91-.34C4.69 10.16 4 11.84 4 14.37c.38 5.6 5.11 7.32 6.81 7.54c2.43.31 5.06-.14 6.95-1.87c2.08-1.93 2.84-5.01 1.72-7.69zm-9.28 5.03c1.44.35 2.18-1.39 1.38-1.95c-.69-.48-1.94-.48-2.63 0c-.84.57-.09 2.3 1.25 1.95z"/></svg>';
    }
    
    return el;
  };
  
  // Update markers with clustering ONLY when map is ready and incidents change
  useEffect(() => {
    if (!mapRef.current || !isMapReady || !incidents) return;
    
    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];
    
    // Convert incidents to GeoJSON features for supercluster
    const points = incidents
      .filter(incident => incident.latitude && incident.longitude)
      .map(incident => ({
        type: 'Feature' as const,
        properties: { ...incident },
        geometry: {
          type: 'Point' as const,
          coordinates: [incident.longitude, incident.latitude]
        }
      }));
    
    // Load points into supercluster
    supercluster.load(points);
    
    // Get map bounds
    const bounds = mapRef.current.getBounds();
    const bbox = [
      bounds.getWest(),
      bounds.getSouth(),
      bounds.getEast(),
      bounds.getNorth()
    ] as [number, number, number, number];
    
    // Get clusters for current map bounds and zoom
    const zoom = Math.floor(mapRef.current.getZoom());
    const clusters = supercluster.getClusters(bbox, zoom);
    
    // Add markers for clusters and individual points
    clusters.forEach(cluster => {
      const [longitude, latitude] = cluster.geometry.coordinates;
      const pointCount = cluster.properties.point_count;
      
      const el = createMarkerElement(cluster, pointCount);
      
      const marker = new mapboxgl.Marker({ element: el })
        .setLngLat([longitude, latitude]);
      
      if (cluster.properties.cluster) {
        // Cluster popup with summary
        marker.setPopup(
          new mapboxgl.Popup({ offset: 25 })
            .setHTML(`
              <div class="p-2">
                <h3 class="font-bold text-sm mb-1">${pointCount} Fire Incidents</h3>
                <p class="text-xs text-gray-600">Click to zoom in for details</p>
              </div>
            `)
        );
        
        // Add click handler to zoom into cluster
        el.addEventListener('click', () => {
          const expansionZoom = Math.min(
            supercluster.getClusterExpansionZoom(cluster.properties.cluster_id),
            16
          );
          mapRef.current?.easeTo({
            center: [longitude, latitude],
            zoom: expansionZoom
          });
        });
      } else {
        // Individual incident popup
        const incident = cluster.properties as FireIncident;
        marker.setPopup(
          new mapboxgl.Popup({ offset: 25 })
            .setHTML(`
              <div class="p-2">
                <h3 class="font-bold text-sm mb-1">${incident.title}</h3>
                <p class="text-xs text-gray-600 mb-1">${incident.location}</p>
                <div class="flex gap-1 mb-1">
                  <span class="px-2 py-0.5 bg-red-100 text-red-800 text-xs rounded">
                    ${incident.status}
                  </span>
                  ${incident.alert_level ? `
                    <span class="px-2 py-0.5 bg-orange-100 text-orange-800 text-xs rounded">
                      ${incident.alert_level}
                    </span>
                  ` : ''}
                </div>
                ${incident.description ? `<p class="text-xs mb-1">${incident.description}</p>` : ''}
                <p class="text-xs text-gray-500">
                  Updated: ${format(new Date(incident.updated), 'MMM d, h:mm a')}
                </p>
              </div>
            `)
        );
      }
      
      marker.addTo(mapRef.current);
      markersRef.current.push(marker);
    });
    
    // Fit map to show all markers if there are any (only on initial load)
    if (incidents.length > 0 && incidents.some(i => i.latitude && i.longitude) && markersRef.current.length === 0) {
      const bounds = new mapboxgl.LngLatBounds();
      incidents.forEach(incident => {
        if (incident.latitude && incident.longitude) {
          bounds.extend([incident.longitude, incident.latitude]);
        }
      });
      
      mapRef.current.fitBounds(bounds, {
        padding: { top: 50, bottom: 50, left: 50, right: 50 },
        maxZoom: 12
      });
    }
  }, [isMapReady, incidents, supercluster]);
  
  // Update clusters when map moves or zooms
  useEffect(() => {
    if (!mapRef.current || !isMapReady) return;
    
    const handleMapUpdate = () => {
      // Re-render markers when map bounds change
      if (incidents) {
        // Clear existing markers
        markersRef.current.forEach(marker => marker.remove());
        markersRef.current = [];
        
        // Get map bounds
        const bounds = mapRef.current!.getBounds();
        const bbox = [
          bounds.getWest(),
          bounds.getSouth(),
          bounds.getEast(),
          bounds.getNorth()
        ] as [number, number, number, number];
        
        // Get clusters for current map bounds and zoom
        const zoom = Math.floor(mapRef.current!.getZoom());
        const clusters = supercluster.getClusters(bbox, zoom);
        
        // Add markers for clusters and individual points
        clusters.forEach(cluster => {
          const [longitude, latitude] = cluster.geometry.coordinates;
          const pointCount = cluster.properties.point_count;
          
          const el = createMarkerElement(cluster, pointCount);
          
          const marker = new mapboxgl.Marker({ element: el })
            .setLngLat([longitude, latitude]);
          
          if (cluster.properties.cluster) {
            // Cluster popup
            marker.setPopup(
              new mapboxgl.Popup({ offset: 25 })
                .setHTML(`
                  <div class="p-2">
                    <h3 class="font-bold text-sm mb-1">${pointCount} Fire Incidents</h3>
                    <p class="text-xs text-gray-600">Click to zoom in for details</p>
                  </div>
                `)
            );
            
            // Add click handler to zoom into cluster
            el.addEventListener('click', () => {
              const expansionZoom = Math.min(
                supercluster.getClusterExpansionZoom(cluster.properties.cluster_id),
                16
              );
              mapRef.current?.easeTo({
                center: [longitude, latitude],
                zoom: expansionZoom
              });
            });
          } else {
            // Individual incident popup
            const incident = cluster.properties as FireIncident;
            marker.setPopup(
              new mapboxgl.Popup({ offset: 25 })
                .setHTML(`
                  <div class="p-2">
                    <h3 class="font-bold text-sm mb-1">${incident.title}</h3>
                    <p class="text-xs text-gray-600 mb-1">${incident.location}</p>
                    <div class="flex gap-1 mb-1">
                      <span class="px-2 py-0.5 bg-red-100 text-red-800 text-xs rounded">
                        ${incident.status}
                      </span>
                      ${incident.alert_level ? `
                        <span class="px-2 py-0.5 bg-orange-100 text-orange-800 text-xs rounded">
                          ${incident.alert_level}
                        </span>
                      ` : ''}
                    </div>
                    ${incident.description ? `<p class="text-xs mb-1">${incident.description}</p>` : ''}
                    <p class="text-xs text-gray-500">
                      Updated: ${format(new Date(incident.updated), 'MMM d, h:mm a')}
                    </p>
                  </div>
                `)
            );
          }
          
          marker.addTo(mapRef.current!);
          markersRef.current.push(marker);
        });
      }
    };
    
    mapRef.current.on('moveend', handleMapUpdate);
    mapRef.current.on('zoomend', handleMapUpdate);
    
    return () => {
      if (mapRef.current) {
        mapRef.current.off('moveend', handleMapUpdate);
        mapRef.current.off('zoomend', handleMapUpdate);
      }
    };
  }, [isMapReady, incidents, supercluster]); // Only run when map is ready AND incidents change
  
  // Update radius circle when location or radius changes
  useEffect(() => {
    if (!mapRef.current || !isMapReady) return;
    
    const center = userLocation ? [userLocation.lng, userLocation.lat] : getDefaultCenter();
    
    // Create circle GeoJSON with proper radius
    const createCircle = (center: number[], radiusInKm: number) => {
      const points = 64;
      const km = radiusInKm;
      const ret = [];
      const distanceX = km / (111.32 * Math.cos(center[1] * Math.PI / 180));
      const distanceY = km / 110.574;
      
      for (let i = 0; i < points; i++) {
        const theta = (i / points) * (2 * Math.PI);
        const x = distanceX * Math.cos(theta);
        const y = distanceY * Math.sin(theta);
        ret.push([center[0] + x, center[1] + y]);
      }
      ret.push(ret[0]);
      
      return {
        type: 'Feature' as const,
        geometry: {
          type: 'Polygon' as const,
          coordinates: [ret]
        },
        properties: {}
      };
    };
    
    const source = mapRef.current.getSource('radius-circle') as mapboxgl.GeoJSONSource;
    if (source) {
      source.setData(createCircle(center, radius));
    }
  }, [isMapReady, radius, userLocation, location]);
  
  return (
    <div className="space-y-4">
      {/* Map container with EXACT structure from working MapContainer */}
      <div className="relative">
        <div 
          ref={mapContainerRef}
          className="h-[400px] w-full rounded-md overflow-hidden bg-gray-100"
          style={{ minHeight: '400px' }} // Critical: ensures container has height!
        />
        
        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-10">
            <div className="flex flex-col items-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="mt-2 text-sm text-muted-foreground">Loading map...</p>
            </div>
          </div>
        )}
        
        {/* Center on location button */}
        {isMapReady && (
          <Button
            variant="secondary"
            size="sm"
            onClick={getUserLocation}
            className="absolute top-3 left-3 z-10 flex items-center gap-1 shadow-md"
          >
            <Locate className="h-3 w-3" />
            My Location
          </Button>
        )}
      </div>
      
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>Showing {incidents?.length || 0} incidents</span>
        <span>Within {radius}km radius</span>
      </div>
    </div>
  );
};

export default memo(FiresMapV2);